import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, FileText, Download } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useData } from "../context/DataContext";
import { CONTACT } from "../mock/mock";
import { WaIcon } from "../components/WhatsAppButton";
import { api, mediaUrl } from "../api";
import { trackEvent } from "../lib/track";
import useSeo from "../hooks/useSeo";

const ProductDetail = () => {
  const { slug } = useParams();
  const { tr, ui, lang } = useLang();
  const { categories } = useData();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    let active = true;
    api.product(slug).then(async (p) => {
      if (!active) return;
      setProduct(p);
      const all = await api.products(p.category).catch(() => []);
      if (active) setRelated(all.filter((x) => x.id !== p.id).slice(0, 4));
    }).catch(() => setProduct(null));
    return () => { active = false; };
  }, [slug]);

  useSeo({
    title: product ? product.name : ui.nav.shop,
    description: product
      ? { en: `${product.name} — a handcrafted natural marble mosaic artwork by HAAS Mosaic.`, tr: `${product.name} — HAAS Mosaic imzalı el yapımı doğal mermer mozaik eseri.` }
      : undefined,
  });

  if (!product) {
    return <div className="min-h-[70vh] flex items-center justify-center bg-[#f7f3ee] text-[#8b8271]">…</div>;
  }

  const cat = categories.find((c) => c.slug === product.category);
  const catName = cat ? tr(cat.name) : "";
  const hasDesc = product.description && (product.description[lang] || product.description.en);
  const fallbackEn = `A handcrafted mosaic masterpiece from our ${cat ? cat.name.en : ""} collection. Every tessera is individually shaped and set by our master artisans using natural marble, creating a unique work of art that brings timeless elegance to any space.`;
  const fallbackTr = `${cat ? cat.name.tr : ""} koleksiyonumuzdan el yapımı bir mozaik başyapıtı. Her tessera, usta zanaatkârlarımız tarafından doğal mermer kullanılarak tek tek şekillendirilir ve yerleştirilir; her mekâna zamansız bir zarafet katan eşsiz bir sanat eseri ortaya çıkar.`;
  const desc = hasDesc ? tr(product.description) : (lang === "tr" ? fallbackTr : fallbackEn);

  const specs = [
    { en: "100% natural marble tesserae", tr: "%100 doğal mermer tessera" },
    { en: "Hand-cut & hand-assembled", tr: "El kesimi ve el montajı" },
    { en: "Custom sizes available", tr: "Özel ölçüler mevcut" },
    { en: "Worldwide shipping", tr: "Dünya çapında kargo" },
  ];

  const waText = encodeURIComponent(`${lang === "tr" ? "Merhaba, bu eser hakkında bilgi almak istiyorum" : "Hello, I would like to inquire about this piece"}: ${product.name}`);

  return (
    <div className="bg-[#f7f3ee]">
      <div className="max-w-[1360px] mx-auto px-5 md:px-8 pt-32 md:pt-40 pb-24">
        <Link to={cat ? `/product-category/${cat.slug}` : "/shop"} className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.16em] text-[#5a5348] hover:text-[#a67c52] transition-colors mb-10">
          <ArrowLeft size={16} /> {ui.backToShop}
        </Link>

        <div className="grid md:grid-cols-2 gap-10 md:gap-16">
          <div className="overflow-hidden bg-white">
            <img src={mediaUrl(product.image)} alt={product.name} className="w-full h-full object-cover" />
          </div>
          <div>
            {cat && <Link to={`/product-category/${cat.slug}`} className="text-[#a67c52] text-[12px] uppercase tracking-luxe hover:underline">{catName}</Link>}
            <h1 className="font-serif text-4xl md:text-5xl text-[#211d18] mt-3 mb-4">{product.name}</h1>
            <p className="text-[#8b8271] text-[13px] uppercase tracking-[0.16em] mb-8">{ui.priceOnRequest}</p>
            <p className="text-[#5a5348] text-[16px] leading-relaxed mb-8">{desc}</p>
            <ul className="space-y-3 mb-10">
              {specs.map((s, i) => (
                <li key={i} className="flex items-center gap-3 text-[14px] text-[#3a352d]"><Check size={16} className="text-[#a67c52]" /> {tr(s)}</li>
              ))}
            </ul>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={`https://wa.me/${CONTACT.whatsapp}?text=${waText}`} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2.5 bg-[#25D366] hover:bg-[#1eb955] text-white text-[13px] uppercase tracking-[0.14em] px-7 py-4 transition-colors">
                <WaIcon size={18} /> {ui.inquire}
              </a>
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-[#211d18] hover:bg-[#a67c52] text-white text-[13px] uppercase tracking-[0.14em] px-7 py-4 transition-colors">
                {ui.requestQuote} <ArrowRight size={16} />
              </Link>
            </div>

            {(product.pdf_tr || product.pdf_en) && (
              <div className="mt-10 pt-8 border-t border-[#e0d6c6]" data-testid="product-catalog-section">
                <h3 className="text-[12px] uppercase tracking-[0.2em] text-[#a67c52] mb-5">{tr({ en: "More", tr: "Daha Fazlası" })}</h3>
                <div className="flex flex-col sm:flex-row gap-3">
                  {product.pdf_tr && (
                    <a
                      href={mediaUrl(product.pdf_tr)}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => trackEvent("catalog")}
                      data-testid="product-catalog-tr"
                      className="group flex items-center justify-between gap-4 flex-1 border border-[#a67c52]/50 hover:border-[#a67c52] hover:bg-[#a67c52] px-5 py-4 transition-colors"
                    >
                      <span className="flex items-center gap-3">
                        <FileText size={22} className="text-[#a67c52] group-hover:text-white transition-colors" />
                        <span className="flex flex-col leading-tight text-left">
                          <span className="text-[10px] uppercase tracking-[0.18em] text-[#8b8271] group-hover:text-white/80 transition-colors">PDF Katalog</span>
                          <span className="text-[13px] uppercase tracking-[0.14em] text-[#211d18] group-hover:text-white transition-colors">Türkçe Katalog</span>
                        </span>
                      </span>
                      <Download size={18} className="text-[#8b8271] group-hover:text-white transition-colors" />
                    </a>
                  )}
                  {product.pdf_en && (
                    <a
                      href={mediaUrl(product.pdf_en)}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => trackEvent("catalog")}
                      data-testid="product-catalog-en"
                      className="group flex items-center justify-between gap-4 flex-1 border border-[#a67c52]/50 hover:border-[#a67c52] hover:bg-[#a67c52] px-5 py-4 transition-colors"
                    >
                      <span className="flex items-center gap-3">
                        <FileText size={22} className="text-[#a67c52] group-hover:text-white transition-colors" />
                        <span className="flex flex-col leading-tight text-left">
                          <span className="text-[10px] uppercase tracking-[0.18em] text-[#8b8271] group-hover:text-white/80 transition-colors">PDF Catalogue</span>
                          <span className="text-[13px] uppercase tracking-[0.14em] text-[#211d18] group-hover:text-white transition-colors">English Catalogue</span>
                        </span>
                      </span>
                      <Download size={18} className="text-[#8b8271] group-hover:text-white transition-colors" />
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-24">
            <h2 className="font-serif text-3xl text-[#211d18] mb-10">{ui.relatedWorks}</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-8">
              {related.map((p) => (
                <Link key={p.id} to={`/urun/${p.slug}`} className="group block">
                  <div className="card-img-zoom overflow-hidden bg-white aspect-square mb-4">
                    <img src={mediaUrl(p.image)} alt={p.name} className="w-full h-full object-cover" />
                  </div>
                  <h3 className="font-serif text-lg text-[#211d18] group-hover:text-[#a67c52] transition-colors">{p.name}</h3>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
